/**
 * useQRGenerator
 *
 * Encapsulates all QR Code Generator state and actions.
 * Components remain pure — all business logic lives here.
 *
 * @param {{ portalId?: number, initialSettings?: object }} options
 *   - portalId       : HubSpot portal ID sent with every generate request
 *   - initialSettings: settings from the retrieveSettings API used to
 *                      pre-populate the form defaults (qr_color, qr_size, qr_margin)
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { message } from 'antd';
import { generateQRCode } from '../api/qrApi';
import { downloadQRCode } from '../services/qrCodeService';
import { uploadHsFile } from '../api/uploadApi';

// Bidirectional map between UTM state field names and URL query param keys
const UTM_FIELD_TO_QUERY = {
  utmSource:   'utm_source',
  utmMedium:   'utm_medium',
  utmCampaign: 'utm_campaign',
  utmContent:  'utm_content',
  utmTerm:     'utm_term',
};

const UTM_QUERY_TO_FIELD = Object.fromEntries(
  Object.entries(UTM_FIELD_TO_QUERY).map(([field, query]) => [query, field])
);

const DEFAULT_STATE = {
  // URL
  url: '',

  // QR Settings (maps to endroid QrCode params)
  size: 300,
  margin: 10,
  errorCorrectionLevel: 'high',
  roundBlockSizeMode: 'margin',

  // Colors
  foregroundColor: '#000000',
  backgroundColor: '#ffffff',

  // Logo (maps to endroid Logo params)
  logo: null,
  logoId: null,
  logoResizeToWidth: 50,
  logoPunchoutBackground: true,

  // Label (maps to endroid Label params)
  labelText: '',
  labelAlignment: 'center',
  labelFontSize: 20,
  labelColor: '#000000',

  // UTM Parameters
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  utmContent: '',
  utmTerm: '',

  // Output
  qrImageUrl: null,
  isLoading: false,
};

export const useQRGenerator = ({ portalId, initialSettings } = {}) => {
  const [state, setState] = useState(DEFAULT_STATE);

  /**
   * Track the active blob URL so we can revoke it before creating a new one,
   * preventing memory leaks from orphaned object URLs.
   */
  const prevBlobUrlRef = useRef(null);

  /**
   * On mount, read `url` and `utm_*` query params from the current route.
   * If present, pre-populate the URL input and UTM fields so the generator
   * is ready to use immediately when navigated to with these params.
   *
   * Supported params: url, utm_source, utm_medium, utm_campaign, utm_content, utm_term
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rawUrl = params.get('url');

    const utmOverrides = {};
    Object.entries(UTM_FIELD_TO_QUERY).forEach(([field, queryKey]) => {
      const val = params.get(queryKey);
      if (val) utmOverrides[field] = val;
    });

    const hasUrl = !!rawUrl;
    const hasUtm = Object.keys(utmOverrides).length > 0;

    if (!hasUrl && !hasUtm) return;

    let finalUrl = rawUrl || '';

    // Append any route-level UTM params directly into the URL query string
    if (finalUrl && hasUtm) {
      try {
        const parsed = new URL(finalUrl);
        Object.entries(UTM_FIELD_TO_QUERY).forEach(([field, queryKey]) => {
          if (utmOverrides[field]) {
            parsed.searchParams.set(queryKey, utmOverrides[field]);
          }
        });
        finalUrl = parsed.toString();
      } catch {
        // Not yet a valid URL — still set the raw value
      }
    }

    // Also extract UTM params that are embedded inside the `url` value itself.
    // e.g. ?url=https://example.com?utm_term=2026  →  utm_term should still populate.
    try {
      const parsedUrl = new URL(finalUrl);
      Object.entries(UTM_FIELD_TO_QUERY).forEach(([field, queryKey]) => {
        if (!utmOverrides[field]) {
          const val = parsedUrl.searchParams.get(queryKey);
          if (val) utmOverrides[field] = val;
        }
      });
    } catch {
      // finalUrl is not yet a valid absolute URL — skip
    }

    setState((prev) => ({
      ...prev,
      url: finalUrl,
      ...utmOverrides,
    }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally runs once on mount

  /**
   * When saved settings arrive from the API, apply them as form defaults.
   * Runs only when `initialSettings` transitions from null → object.
   */
  useEffect(() => {
    if (!initialSettings) return;
    setState((prev) => ({
      ...prev,
      size:            initialSettings.size   ?? prev.size,
      margin:          initialSettings.margin  ?? prev.margin,
      foregroundColor: initialSettings.color   ?? prev.foregroundColor,
      backgroundColor: initialSettings.background   ?? prev.backgroundColor,
      labelText:       initialSettings.label?.text ?? prev.labelText,
      labelAlignment:  initialSettings.label?.alignment ?? prev.labelAlignment,
      labelFontSize:   initialSettings.label?.fontSize ?? prev.labelFontSize,
      labelColor:      initialSettings.label?.color ?? prev.labelColor,
      url:             initialSettings.url ?? prev.url,
      utmSource:       initialSettings.utmParams?.utm_source ?? prev.utmSource,
      utmMedium:       initialSettings.utmParams?.utm_medium ?? prev.utmMedium,
      utmCampaign:     initialSettings.utmParams?.utm_campaign ?? prev.utmCampaign,
      utmContent:      initialSettings.utmParams?.utm_content ?? prev.utmContent,
      utmTerm:         initialSettings.utmParams?.utm_term ?? prev.utmTerm,
      logoInfo: initialSettings.logo ? {
        id: initialSettings.logo.id,
        resizeWidth: initialSettings.logo.resizeWidth,
      } : null,
    }));
  }, [initialSettings]);

  /** Revoke a blob URL to free browser memory, if it is one. */
  const revokeBlobUrl = useCallback((url) => {
    if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
  }, []);

  /** Clean up the active blob URL when the host component unmounts. */
  useEffect(() => {
    return () => revokeBlobUrl(prevBlobUrlRef.current);
  }, [revokeBlobUrl]);

  const updateField = useCallback((field, value) => {
    if (field === 'url') {
      try {
        // Valid URL: sync ALL UTM fields — set found ones, clear missing ones
        const parsed = new URL(value);
        const utmSync = Object.fromEntries(
          Object.entries(UTM_QUERY_TO_FIELD).map(([queryKey, stateKey]) => [
            stateKey,
            parsed.searchParams.get(queryKey) ?? '',
          ])
        );
        setState((prev) => ({ ...prev, url: value, ...utmSync }));
      } catch {
        // URL is empty → clear all UTM fields; still mid-typing → leave UTMs alone
        const utmClears = !value.trim()
          ? Object.fromEntries(Object.keys(UTM_FIELD_TO_QUERY).map((k) => [k, '']))
          : {};
        setState((prev) => ({ ...prev, url: value, ...utmClears }));
      }
      return;
    }

    // UTM field edited directly → reflect change back into the URL query string
    if (field in UTM_FIELD_TO_QUERY) {
      setState((prev) => {
        let updatedUrl = prev.url;
        try {
          const parsed = new URL(prev.url);
          if (value) {
            parsed.searchParams.set(UTM_FIELD_TO_QUERY[field], value);
          } else {
            parsed.searchParams.delete(UTM_FIELD_TO_QUERY[field]);
          }
          updatedUrl = parsed.toString();
        } catch {
          // URL not yet valid — update only the UTM field, leave url as-is
        }
        return { ...prev, [field]: value, url: updatedUrl };
      });
      return;
    }

    // When logo changes (cleared or replaced with new file) reset the cached upload id
    if (field === 'logo') {
      setState((prev) => ({ ...prev, logo: value || null, logoId: null }));
      return;
    }

    setState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const generateQR = useCallback(async () => {
    const {
      url,
      size,
      margin,
      errorCorrectionLevel,
      roundBlockSizeMode,
      foregroundColor,
      backgroundColor,
      logo,
      logoId,
      logoResizeToWidth,
      logoPunchoutBackground,
      labelText,
      labelAlignment,
      labelFontSize,
      labelColor,
      utmSource,
      utmMedium,
      utmCampaign,
      utmContent,
      utmTerm,
    } = state;

    // ── Client-side validation ──────────────────────────────────────────────
    if (!url.trim()) {
      message.warning('Please enter a URL to generate a QR code.');
      return;
    }
    try {
      new URL(url.trim());
    } catch {
      message.error('Please enter a valid URL (e.g. https://example.com).');
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, qrImageUrl: null }));

    // Revoke the previous blob before a new one is created
    revokeBlobUrl(prevBlobUrlRef.current);
    prevBlobUrlRef.current = null;

    // Strip UTM params from the URL before sending — they are passed separately
    // as dedicated fields (utmSource, utmMedium, …) so the backend never sees
    // duplicates and the stored URL stays clean (e.g. https://transfunnel.com).
    let cleanUrl = url.trim();
    try {
      const parsed = new URL(cleanUrl);
      Object.values(UTM_FIELD_TO_QUERY).forEach((queryKey) =>
        parsed.searchParams.delete(queryKey)
      );
      cleanUrl = parsed.toString();
      console.log(cleanUrl);
    } catch {
      // Not a valid URL — validation above already caught this; proceed as-is
    }

    try {

      const result = await generateQRCode({
        url: cleanUrl,
        size,
        margin,
        errorCorrectionLevel,
        roundBlockSizeMode,
        foregroundColor,
        backgroundColor,
        logoId,
        logoResizeToWidth,
        labelText,
        labelAlignment,
        labelFontSize,
        labelColor,
        utmSource,
        utmMedium,
        utmCampaign,
        utmContent,
        utmTerm,
      });

      prevBlobUrlRef.current = result.qrImageUrl;

      setState((prev) => ({
        ...prev,
        isLoading: false,
        qrImageUrl: result.qrImageUrl,
      }));
    } catch (err) {
      setState((prev) => ({ ...prev, isLoading: false }));
      message.error(err.message || 'Failed to generate QR code. Please try again.');
    }
  }, [state, portalId, revokeBlobUrl]);

  const downloadQR = useCallback(async () => {
    if (!state.qrImageUrl) return;
    try {
      await downloadQRCode(state.qrImageUrl);
      message.success('QR code downloaded!');
    } catch (err) {
      message.error(err.message || 'Failed to download QR code.');
    }
  }, [state.qrImageUrl]);

  /**
   * Uploads a logo file immediately and caches the returned id in state.
   * Called by CustomizePanel's "Upload Logo" button.
   *
   * @param {File} file
   * @returns {Promise<number|null>} the uploaded file id
   */
  const uploadLogo = useCallback(async (file) => {
    const fileInfo = await uploadHsFile(file);
    const id = fileInfo.hsFileId ?? null;
    setState((prev) => ({ ...prev, logoId: id }));
    return id;
  }, []);

  const resetQR = useCallback(() => {
    revokeBlobUrl(prevBlobUrlRef.current);
    prevBlobUrlRef.current = null;
    setState(DEFAULT_STATE);
    // Re-apply server settings after reset so defaults stay correct
    if (initialSettings) {
      setState((prev) => ({
        ...prev,
        size:            initialSettings.qr_size   ?? prev.size,
        margin:          initialSettings.qr_margin  ?? prev.margin,
        foregroundColor: initialSettings.qr_color   ?? prev.foregroundColor,
      }));
    }
  }, [revokeBlobUrl, initialSettings]);

  return {
    ...state,
    updateField,
    generateQR,
    downloadQR,
    resetQR,
    uploadLogo,
  };
};
